import { useQuery } from "@tanstack/react-query";
import { CONNECTIVITY_STATES, DEVICE_STATUSES } from "@/contracts";

import { devicesQuery, siteOptionsQuery, type DeviceFilters } from "@/api/queries";
import { hasRole, useAuth } from "@/auth/auth-context";
import { useApiClient } from "@/lib/api-context";
import { useListFilters } from "@/lib/use-list-filters";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { SelectField } from "@/components/ui/field";
import { useTranslation } from "react-i18next";
import { ErrorBlock, LoadingBlock, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { DataTable, Td, Th } from "@/components/ui/table";
import { CONNECTIVITY, DEVICE_STATUS, DEVICE_TABLE_COLUMNS } from "./device-labels";
import { DeviceRows } from "./device-rows";

export function DevicesPage() {
  const { t } = useTranslation("devices");
  const { t: tCommon } = useTranslation("common");
  const client = useApiClient();
  const { user } = useAuth();
  const canRegister = hasRole(user, ["ADMIN", "SUPER_ADMIN"]);

  const { value: filterValue, page, setFilter, setPage } = useListFilters();

  const filters: DeviceFilters = {
    ...(filterValue("search") ? { search: filterValue("search") } : {}),
    ...(filterValue("connectivity") ? { connectivity: filterValue("connectivity") } : {}),
    ...(filterValue("status") ? { status: filterValue("status") } : {}),
    ...(filterValue("siteId") ? { siteId: filterValue("siteId") } : {}),
    page,
  };

  const devices = useQuery(devicesQuery(client, filters));
  const sites = useQuery(siteOptionsQuery(client));

  return (
    <>
      <PageHeader
        title={t("listTitle")}
        description={t("listDescription")}
        action={
          // Hidden for a plain user, and refused again by the API: this only
          // removes a button that would always fail.
          canRegister ? <Button to="/devices/new">{t("register")}</Button> : null
        }
      />

      <Card className="mb-4">
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SearchInput
              value={filters.search ?? ""}
              placeholder={t("searchPlaceholder")}
              onChange={(value) => setFilter("search", value)}
            />

            <SelectField
              label={t("filterConnectivity")}
              value={filters.connectivity ?? ""}
              onChange={(value) => setFilter("connectivity", value)}
              data={[
                { value: "", label: t("filterAll") },
                ...CONNECTIVITY_STATES.map((state) => ({
                  value: state,
                  label: CONNECTIVITY[state].label,
                })),
              ]}
            />

            <SelectField
              label={t("filterStatus")}
              value={filters.status ?? ""}
              onChange={(value) => setFilter("status", value)}
              data={[
                { value: "", label: t("filterAll") },
                ...DEVICE_STATUSES.map((status) => ({
                  value: status,
                  label: DEVICE_STATUS[status].label,
                })),
              ]}
            />

            <SelectField
              label={t("filterSite")}
              value={filters.siteId ?? ""}
              onChange={(value) => setFilter("siteId", value)}
              data={[
                { value: "", label: t("filterAll") },
                ...(sites.data?.data ?? []).map((site) => ({
                  value: site.id,
                  label: site.name,
                })),
              ]}
            />
          </div>
        </CardBody>
      </Card>

      {devices.isPending ? <LoadingBlock label={t("loading")} /> : null}

      {devices.isError ? (
        <ErrorBlock error={devices.error} onRetry={() => void devices.refetch()} />
      ) : null}

      {devices.data ? (
        <Card>
          <DataTable caption={t("caption")}>
            <thead>
              <tr>
                {DEVICE_TABLE_COLUMNS.map((column) => (
                  <Th key={column.label} numeric={column.numeric}>
                    {column.label}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              <DeviceRows devices={devices.data.data} />
            </tbody>
            <tfoot>
              <tr>
                <Td colSpan={DEVICE_TABLE_COLUMNS.length} className="border-b-0">
                  <Pagination
                    page={devices.data.pagination.page}
                    totalPages={devices.data.pagination.totalPages}
                    total={devices.data.pagination.total}
                    noun={{ one: tCommon("noun.device.one"), other: tCommon("noun.device.other") }}
                    onChange={setPage}
                  />
                </Td>
              </tr>
            </tfoot>
          </DataTable>
        </Card>
      ) : null}
    </>
  );
}
